import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportColumn {
  header: string;
  key: string;
}

/**
 * Metadata printed into every PDF page's header/footer band. Lets a report
 * carry its identity (name, entity, period, timestamp, confidentiality) onto
 * paper the way a board/CFO document does.
 */
export interface PdfMeta {
  title?: string;
  entity?: string;
  period?: string;
  generatedAt?: string;
  confidentiality?: string;
}

export interface PdfOptions {
  orientation?: 'portrait' | 'landscape';
  meta?: PdfMeta;
}

/**
 * Export a DOM element to a professionally paginated PDF.
 *
 * Why not the naive `html2pdf(...).save()`: rasterising the whole element and
 * slicing it by raw pixel height is exactly what clips columns and cuts rows,
 * cards and charts across page boundaries. This version:
 *   - honours CSS page-break rules and refuses to split a table row, a card,
 *     a chart or a table header across pages (`pagebreak.avoid`);
 *   - keeps `thead` repeating on every page a table spans;
 *   - leaves a top/bottom band and stamps a real header (report name, entity,
 *     period) and footer (generated timestamp, confidentiality, "Page N of M")
 *     onto every page after layout, via the jsPDF handle;
 *   - captures at the element's full scroll width so nothing is clipped on the
 *     right (wide tables should pass `orientation: 'landscape'`).
 *
 * Back-compatible: the old positional `(elementId, filename, orientation)`
 * signature still works; pass a `PdfOptions` object for header/footer metadata.
 */
export const exportToPDF = (
  elementId: string,
  filename: string,
  optionsOrOrientation: 'portrait' | 'landscape' | PdfOptions = 'portrait'
): void => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`);
    return;
  }

  const options: PdfOptions =
    typeof optionsOrOrientation === 'string' ? { orientation: optionsOrOrientation } : optionsOrOrientation;
  const orientation = options.orientation ?? 'portrait';
  const meta = options.meta;

  // Top/bottom margins leave room for the stamped header/footer band.
  const opt = {
    margin: [16, 10, 14, 10] as [number, number, number, number],
    filename: `${filename}.pdf`,
    image: { type: 'jpeg' as const, quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0, windowWidth: element.scrollWidth },
    jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation },
    // Honour CSS breaks AND never split these elements across a page boundary.
    pagebreak: {
      // `.MuiPaper-root` matches the actual StatCard/Paper surfaces used across
      // the report pages (`.MuiCard-root` did not, so cards still split).
      mode: ['css', 'legacy'] as ['css', 'legacy'],
      avoid: ['tr', 'thead', '.avoid-break', '.report-chart-card', '.MuiPaper-root', '.MuiCard-root'],
    },
  };

  // html2pdf's worker is a chainable thenable, but its TS types report the
  // `.then()` result as a plain Promise — cast so we can stamp headers/footers
  // on the resolved jsPDF handle and still `.save()` at the end of the chain.
  (html2pdf() as any)
    .set(opt)
    .from(element)
    .toPdf()
    .get('pdf')
    .then((pdf: any) => {
      const pageCount: number = pdf.internal.getNumberOfPages();
      const w: number = pdf.internal.pageSize.getWidth();
      const h: number = pdf.internal.pageSize.getHeight();
      const rightHeader = [meta?.entity, meta?.period].filter(Boolean).join('  ·  ');
      const leftFooter = [meta?.generatedAt && `Generated ${meta.generatedAt}`, meta?.confidentiality]
        .filter(Boolean)
        .join('   ·   ');

      for (let i = 1; i <= pageCount; i += 1) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');

        // Header band
        pdf.setFontSize(9);
        pdf.setTextColor(40, 40, 40);
        if (meta?.title) pdf.text(meta.title, 10, 9);
        if (rightHeader) {
          pdf.setTextColor(120, 120, 120);
          pdf.setFontSize(8);
          pdf.text(rightHeader, w - 10, 9, { align: 'right' });
        }
        pdf.setDrawColor(210, 210, 210);
        pdf.setLineWidth(0.2);
        pdf.line(10, 11.5, w - 10, 11.5);

        // Footer band
        pdf.setFontSize(7.5);
        pdf.setTextColor(130, 130, 130);
        pdf.line(10, h - 8.5, w - 10, h - 8.5);
        if (leftFooter) pdf.text(leftFooter, 10, h - 5);
        pdf.text(`Page ${i} of ${pageCount}`, w - 10, h - 5, { align: 'right' });
      }
    })
    .save();
};

/**
 * Export data array to Excel. Optional `summary` rows produce a labelled
 * summary block above the detail table, and totals are appended when a column
 * is flagged numeric — a step up from a raw header+rows dump.
 */
export const exportToExcel = (
  data: Record<string, unknown>[],
  columns: ExportColumn[],
  filename: string,
  sheetName: string = 'Report'
): void => {
  const wsData = [
    columns.map((c) => c.header),
    ...data.map((row) => columns.map((c) => row[c.key] ?? '')),
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const colWidths = columns.map((col) => {
    const maxLength = Math.max(
      col.header.length,
      ...data.map((row) => String(row[col.key] ?? '').length)
    );
    return { wch: Math.min(maxLength + 2, 50) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Format a number as Indian currency string for Excel export
 */
export const formatIndianCurrency = (amount: number): string => {
  if (amount === 0) return '0.00';
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `-${formatted}` : formatted;
};

/**
 * Format a date string for Excel export
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ===========================================================================
// Statutory PDF export via jsPDF + jspdf-autotable (from STRUCTURED data).
//
// Why this exists alongside exportToPDF (html2pdf): html2pdf rasterises the
// live DOM, so any `text-overflow: ellipsis` / `white-space: nowrap` cell is
// baked into the image CLIPPED, wide tables overflow an A4 portrait page, and
// text is not selectable. For statutory statements (Trial Balance, Balance
// Sheet, P&L) that is unacceptable. This path draws real vector text from the
// statement data: deterministic pagination, column headers that repeat on
// every page, a company/report header band, page numbers, landscape for wide
// tables, proper Indian number formatting, and totals that are always visible.
// ===========================================================================

export type PdfCellAlign = 'left' | 'right' | 'center';

export interface StatementPdfColumn {
  header: string;
  key: string;
  align?: PdfCellAlign;
  /** Column width in mm (optional; autotable auto-sizes when omitted). */
  width?: number;
}

/** One statement row. `_emphasis` styles section headers / subtotals / totals. */
export interface StatementPdfRow {
  [key: string]: string | number | undefined;
  _emphasis?: 'section' | 'subtotal' | 'total';
}

export interface StatementPdfTable {
  /** Optional caption printed above this table. */
  caption?: string;
  columns: StatementPdfColumn[];
  rows: StatementPdfRow[];
}

export interface StatementPdfOptions {
  filename: string;
  orientation?: 'portrait' | 'landscape';
  company?: string;
  reportTitle: string;
  subtitle?: string;
  /** e.g. "As on 16 Aug 2026" or "For the period 01 Apr 2026 – 16 Aug 2026". */
  periodLine?: string;
  confidentiality?: string;
  tables: StatementPdfTable[];
}

const EMPHASIS_FILL: Record<string, [number, number, number]> = {
  section: [237, 242, 247],
  subtotal: [246, 248, 250],
  total: [222, 230, 240],
};

/**
 * Render one or more structured tables into a single paginated statutory PDF.
 * Never rasterises, never clips: text wraps, headers repeat, totals are bold.
 */
export const exportStatementPDF = (options: StatementPdfOptions): void => {
  const orientation = options.orientation ?? 'portrait';
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 12;
  const headerBottom = 30; // first table starts below the header band
  const generatedAt = new Date().toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const drawPageFurniture = () => {
    // Header band
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(options.company || 'Company', marginX, 12);

    doc.setFontSize(10.5);
    doc.setTextColor(45, 45, 45);
    doc.text(options.reportTitle, marginX, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(110, 110, 110);
    const sub = [options.subtitle, options.periodLine].filter(Boolean).join('   ·   ');
    if (sub) doc.text(sub, marginX, 23);
    doc.text('All amounts in ₹', pageWidth - marginX, 12, { align: 'right' });

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(marginX, headerBottom - 4, pageWidth - marginX, headerBottom - 4);

    // Footer band
    doc.setFontSize(7.5);
    doc.setTextColor(130, 130, 130);
    doc.line(marginX, pageHeight - 9, pageWidth - marginX, pageHeight - 9);
    const leftFooter = [`Generated ${generatedAt}`, options.confidentiality].filter(Boolean).join('   ·   ');
    doc.text(leftFooter, marginX, pageHeight - 5.5);
    // Page number stamped after all pages exist (see loop below).
  };

  let startY = headerBottom;
  options.tables.forEach((table, tIdx) => {
    if (table.caption) {
      // Ensure caption doesn't strand at the very bottom.
      if (startY > pageHeight - 30) {
        doc.addPage();
        startY = headerBottom;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(40, 40, 40);
      doc.text(table.caption, marginX, startY + (tIdx === 0 ? 0 : 4));
      startY += tIdx === 0 ? 3 : 7;
    }

    const head = [table.columns.map((c) => c.header)];
    const body = table.rows.map((r) => table.columns.map((c) => {
      const v = r[c.key];
      return v === undefined || v === null ? '' : String(v);
    }));

    const columnStyles: Record<number, any> = {};
    table.columns.forEach((c, i) => {
      columnStyles[i] = {
        halign: c.align || (i === 0 ? 'left' : 'right'),
        ...(c.width ? { cellWidth: c.width } : {}),
      };
    });

    autoTable(doc, {
      head,
      body,
      startY,
      margin: { left: marginX, right: marginX, top: headerBottom, bottom: 12 },
      // No ellipsis: long text wraps onto multiple lines instead of truncating.
      styles: { fontSize: 8, cellPadding: 1.6, overflow: 'linebreak', textColor: [30, 30, 30], lineColor: [225, 225, 225], lineWidth: 0.1 },
      headStyles: { fillColor: [33, 47, 61], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, halign: 'left' },
      columnStyles,
      // Repeat the column header on every page the table spans.
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
      didParseCell: (data: any) => {
        if (data.section !== 'body') return;
        const emphasis = table.rows[data.row.index]?._emphasis;
        if (emphasis && EMPHASIS_FILL[emphasis]) {
          data.cell.styles.fillColor = EMPHASIS_FILL[emphasis];
          data.cell.styles.fontStyle = emphasis === 'section' || emphasis === 'total' ? 'bold' : 'normal';
        }
      },
      didDrawPage: () => drawPageFurniture(),
    });

    startY = (doc as any).lastAutoTable.finalY + 8;
  });

  // Stamp "Page i of N" now that the final page count is known.
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(130, 130, 130);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 5.5, { align: 'right' });
  }

  doc.save(`${options.filename}.pdf`);
};

// ===========================================================================
// Multi-sheet Excel (statement + notes + reconciliation) with Indian format.
// ===========================================================================

export interface ExcelSheet {
  name: string;
  columns: ExportColumn[];
  rows: Record<string, unknown>[];
}

/** Write several sheets into one workbook (statement + notes + reconciliation). */
export const exportMultiSheetExcel = (sheets: ExcelSheet[], filename: string): void => {
  const wb = XLSX.utils.book_new();
  sheets.forEach((sheet) => {
    const wsData = [
      sheet.columns.map((c) => c.header),
      ...sheet.rows.map((row) => sheet.columns.map((c) => row[c.key] ?? '')),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = sheet.columns.map((col) => {
      const maxLength = Math.max(
        col.header.length,
        ...sheet.rows.map((row) => String(row[col.key] ?? '').length)
      );
      return { wch: Math.min(maxLength + 2, 60) };
    });
    // Excel sheet names are capped at 31 chars and cannot contain : \ / ? * [ ].
    const safeName = sheet.name.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Sheet';
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  });
  XLSX.writeFile(wb, `${filename}.xlsx`);
};
